import React, { useEffect, useMemo, useState } from 'react'
import { FaChevronLeft, FaChevronRight, FaDirections, FaEllipsisH, FaFlagCheckered, FaList, FaLocationArrow, FaMagic, FaMap, FaMapMarkerAlt, FaPhoneAlt, FaPlay, FaPlus, FaStore } from 'react-icons/fa'
import { Banner, ConfirmSheet, Empty, Field, FoBtn, Pill, Ring, Seg, Sheet, ChipSelect } from './fieldUi'
import FieldMap from './FieldMap'
import NearbyPanel from './NearbyPanel'
import { useField } from './fieldContext'
import { DEPOT, clockLabel, fetchRoadRoute, fmtMiles, fmtMinutes, mapsNavigateUrl, mapsRouteUrl, orderNearest, pathMiles, rankNearby, telUrl } from '../geo'
import { addDays, fmtDate, fmtDateLong, money, todayIso, useNarrow, weekday } from '../FieldOpsKit'
import { RESCHEDULE_REASONS, MISS_REASONS } from '../demoData'
import { nearbyCandidates, unroutedStores, workDate } from '../store'
import { clockOfStamp } from '../geo'

const VISIT_RESCHED = ['Store asked', 'Route changed', 'Vehicle issue', 'Other']

const shiftDay = (date, dir) => {
    let d = addDays(date, dir)
    while (weekday(d) === 0 || weekday(d) === 6) d = addDays(d, dir)
    return d
}
const greeting = () => { const h = new Date().getHours(); return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening' }

export default function RouteScreen() {
    const f = useField()
    const { stops, summary, kind, date, isToday, day, gps, nav, selected, setSelected, state } = f
    const narrow = useNarrow(992)
    const [view, setView] = useState('list')
    const [menu, setMenu] = useState(null)
    const [skip, setSkip] = useState(null)
    const [resched, setResched] = useState(null)
    const [addOpen, setAddOpen] = useState(false)
    const [optOpen, setOptOpen] = useState(false)
    const [showNearby, setShowNearby] = useState(false)
    const [focus, setFocus] = useState(null)
    const [road, setRoad] = useState(null)
    const [fitKey, setFitKey] = useState(0)

    const nextStop = stops.filter((s) => s.state === 'current')[0] || stops.filter((s) => s.state === 'pending')[0] || null
    const pendingStops = stops.filter((s) => s.state === 'pending')
    const doneStops = stops.filter((s) => s.state === 'done')
    const anchor = (stops.filter((s) => s.state === 'current')[0]) || gps.pos || (doneStops.length ? doneStops[doneStops.length - 1] : null) || (kind === 'driver' ? DEPOT : null)
    const started = !!(day && day.startedAt)
    const submitted = !!(day && day.submitted)
    const mapVisible = !narrow || view === 'map'

    // reset the highlighted stop and refit when the day or the list of stops changes
    const stopIds = stops.map((s) => s.id).join(',')
    useEffect(() => { setSelected(null); setFitKey((k) => k + 1) }, [date, stopIds]) // eslint-disable-line react-hooks/exhaustive-deps

    // driving geometry for the part of the route that is still ahead (OSRM; straight dashed line while it loads / if it fails)
    const aheadPoints = useMemo(() => {
        if (!anchor) return []
        const pts = [{ lat: anchor.lat, lng: anchor.lng }].concat(pendingStops.map((s) => ({ lat: s.lat, lng: s.lng })))
        if (kind === 'driver') pts.push({ lat: DEPOT.lat, lng: DEPOT.lng })
        return pts.slice(0, 24)
    }, [anchor ? anchor.lat : 0, anchor ? anchor.lng : 0, pendingStops.map((s) => s.id).join(','), kind]) // eslint-disable-line react-hooks/exhaustive-deps
    const aheadKey = aheadPoints.map((p) => p.lat.toFixed(3) + p.lng.toFixed(3)).join('|')
    useEffect(() => {
        setRoad(null)
        if (!mapVisible || aheadPoints.length < 2) return undefined
        let alive = true
        const t = setTimeout(() => { fetchRoadRoute(aheadPoints).then((r) => { if (alive) setRoad(r) }) }, 350)
        return () => { alive = false; clearTimeout(t) }
    }, [aheadKey, mapVisible]) // eslint-disable-line react-hooks/exhaustive-deps

    /* ---------- map data ---------- */
    const pins = stops.map((s) => ({ id: s.id, lat: s.lat, lng: s.lng, text: s.state === 'done' ? '✓' : s.num, tone: s.tone, selected: selected === s.id, pulse: !!nextStop && nextStop.id === s.id, size: nextStop && nextStop.id === s.id ? 'lg' : 'md', title: s.name }))
    const lines = []
    if (doneStops.length > 1) lines.push({ id: 'done', coords: doneStops.map((s) => [s.lat, s.lng]), color: '#10b981', weight: 4, opacity: 0.7, dashed: true })
    if (aheadPoints.length > 1) {
        if (road && road.coords) lines.push({ id: 'ahead', coords: road.coords, color: '#2563EB', weight: 5, casing: true })
        else lines.push({ id: 'ahead-s', coords: aheadPoints.map((p) => [p.lat, p.lng]), color: '#2563EB', weight: 4, dashed: true, opacity: 0.8 })
    }
    const nearbyAnchor = anchor ? { lat: anchor.lat, lng: anchor.lng } : null
    const dotItems = useMemo(() => {
        if (kind !== 'rep' || !showNearby || !nearbyAnchor) return []
        const cands = nearbyCandidates(state.sales, f.actor.rep, stops.filter((s) => s.kind === 'visit').map((s) => s.store.id), f.vIdx, f.oIdx, unroutedStores())
        return rankNearby(nearbyAnchor, cands, 15).slice(0, 12)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showNearby, state.sales, stopIds, nearbyAnchor ? nearbyAnchor.lat : 0, nearbyAnchor ? nearbyAnchor.lng : 0])
    const dots = dotItems.map((r) => ({ id: 'n:' + r.store.id, lat: r.store.lat, lng: r.store.lng, title: r.store.name, selected: selected === 'n:' + r.store.id }))

    const selectedStop = stops.filter((s) => s.id === selected)[0] || null
    const selectedNearby = selected && String(selected).indexOf('n:') === 0 ? dotItems.filter((r) => 'n:' + r.store.id === selected)[0] || null : null

    /* ---------- actions ---------- */
    const open = (s, checkin) => nav.push({ type: 'stop', kind: s.kind, id: s.id, checkin: !!checkin })
    const pick = (id) => { setSelected(id); const s = stops.filter((x) => x.id === id)[0]; if (s) setFocus({ lat: s.lat, lng: s.lng, zoom: 15, k: Date.now() }) }
    const move = (s, dir) => {
        if (s.kind === 'visit') f.dispatch({ type: 'VISIT_MOVE', rep: f.actor.rep, date: date, visitId: s.id, dir: dir })
        else f.dispatch({ type: 'DELIVERY_MOVE', date: date, driverId: f.actor.driverId, id: s.id, dir: dir })
    }
    const canMove = (s, dir) => { const i = pendingStops.findIndex((x) => x.id === s.id); return i >= 0 && i + dir >= 0 && i + dir < pendingStops.length }
    const doSkip = (r) => { f.dispatch({ type: 'VISIT_SKIP', visitId: skip.id, reason: r.reason, note: r.note }); f.toast(skip.name + ' skipped.', 'warn'); setSkip(null) }

    const optimize = () => {
        if (pendingStops.length < 3) { f.toast('You need at least 3 stops left to optimize.', 'warn'); return }
        const from = anchor ? { lat: anchor.lat, lng: anchor.lng } : pendingStops[0]
        const before = pathMiles(pendingStops, from)
        const best = orderNearest(pendingStops.map((s) => ({ id: s.id, lat: s.lat, lng: s.lng, name: s.name })), from)
        const after = pathMiles(best, from)
        setOptOpen({ before: before, after: after, order: best })
    }

    /* ---------- hero ---------- */
    const left = summary.left
    const allDone = stops.length > 0 && left === 0
    const nextNum = nextStop ? nextStop.num : 0
    const heroCta = (() => {
        if (stops.length === 0) return null
        if (!isToday) return <Banner tone="info">Planned route. You can start it on {fmtDate(date)}.</Banner>
        if (submitted) return <div className="fo-hero__sent">Day report sent ✓</div>
        if (!started && f.state.field.positions[f.actor.key] === undefined && doneStops.length === 0) return <FoBtn kind="success" big block icon={<FaPlay />} onClick={() => nav.push({ type: 'startday' })}>Start my day</FoBtn>
        if (allDone) return <FoBtn kind="success" big block icon={<FaFlagCheckered />} onClick={() => nav.push({ type: 'endday' })}>End day &amp; send report</FoBtn>
        if (nextStop) {
            return (
                <div className="fo-hero__nextrow">
                    <FoBtn kind="primary" big block icon={nextStop.state === 'current' ? <FaStore /> : <FaDirections />} onClick={() => (nextStop.state === 'current' ? open(nextStop) : window.open(mapsNavigateUrl(nextStop.lat, nextStop.lng), '_blank'))}>
                        {nextStop.state === 'current' ? 'Continue at ' + nextStop.name : 'Navigate to #' + nextNum + ' ' + nextStop.name}
                    </FoBtn>
                    {nextStop.state !== 'current' ? <FoBtn kind="ghost" big icon={<FaStore />} onClick={() => open(nextStop, true)}>{kind === 'rep' ? 'Check in' : 'Arrived'}</FoBtn> : null}
                </div>
            )
        }
        return null
    })()

    const routeNames = kind === 'rep' ? Array.from(new Set(stops.filter((s) => s.kind === 'visit').map((s) => s.store.route))).join(' · ') : 'Deliveries from ' + DEPOT.name.replace(' (demo)', '')
    const kpis = kind === 'rep'
        ? [['Orders today', money(summary.money)], ['Stops left', String(left)], ['Driving', summary.miles + ' mi'], ['Done around', summary.endEta ? clockLabel(summary.endEta) : '—']]
        : [['Weight', Math.round(stops.reduce((a, s) => a + (s.weightLb || 0), 0)).toLocaleString('en-US') + ' lb'], ['Stops left', String(left)], ['Driving', summary.miles + ' mi'], ['To collect', money(stops.filter((s) => s.state !== 'done' && s.ref && s.ref.terms === 'COD').reduce((a, s) => a + s.amount, 0))]]

    const hero = (
        <div className="fo-hero">
            <div className="fo-hero__top">
                <div>
                    <small>{isToday ? greeting() + ', ' + f.actor.name.split(' ')[0] : 'Planning'}</small>
                    <h2>{fmtDateLong(date).replace(/, \d{4}$/, '')}</h2>
                    <p>{stops.length} {kind === 'rep' ? 'visits' : 'deliveries'}{routeNames ? ' · ' + routeNames : ''}</p>
                </div>
                <div className="fo-hero__date">
                    <button type="button" aria-label="Previous day" disabled={date <= workDate()} onClick={() => f.setDate(shiftDay(date, -1))}><FaChevronLeft /></button>
                    <span>{isToday ? 'Today' : fmtDate(date).split(',')[0]}</span>
                    <button type="button" aria-label="Next day" disabled={date >= addDays(workDate(), 7)} onClick={() => f.setDate(shiftDay(date, 1))}><FaChevronRight /></button>
                </div>
            </div>
            <div className="fo-hero__stats">
                <Ring value={summary.done} max={Math.max(1, summary.total - summary.missed)} label={summary.done + '/' + Math.max(0, summary.total - summary.missed)} sub={kind === 'rep' ? 'visits' : 'delivered'} size={92} stroke={9} />
                <div className="fo-hero__kpis">{kpis.map((k) => <div key={k[0]}><span>{k[0]}</span><strong>{k[1]}</strong></div>)}</div>
            </div>
            {heroCta ? <div className="fo-hero__cta">{heroCta}</div> : null}
            {isToday && started && !submitted && !allDone && stops.length > 0 ? <button type="button" className="fo-hero__link" onClick={() => nav.push({ type: 'endday' })}>End my day early…</button> : null}
        </div>
    )

    const weekendNote = isToday && (weekday(todayIso()) === 0 || weekday(todayIso()) === 6)

    /* ---------- cards ---------- */
    const card = (s) => {
        const isNext = nextStop && nextStop.id === s.id
        if (s.state === 'done') {
            const when = s.ref.checkInAt || s.ref.doneAt
            return (
                <div key={s.id} className={'fo-stop fo-stop--done' + (selected === s.id ? ' is-sel' : '')} onClick={() => { pick(s.id); if (narrow || true) open(s) }}>
                    <span className={'fo-num fo-num--' + s.tone}>✓</span>
                    <div className="fo-stop__main"><strong>{s.name}</strong><span>{s.label}{s.detail ? ' · ' + s.detail : ''}</span></div>
                    <div className="fo-stop__time">{when ? clockOfStamp(when) : ''}{s.ref.dur ? <small>{s.ref.dur} min</small> : null}</div>
                    {s.pending ? <Pill tone="amber">To send</Pill> : null}
                </div>
            )
        }
        if (s.state === 'missed') {
            return (
                <div key={s.id} className="fo-stop fo-stop--missed" onClick={() => open(s)}>
                    <span className="fo-num fo-num--red">!</span>
                    <div className="fo-stop__main"><strong>{s.name}</strong><span>{s.label}{s.detail ? ' · ' + s.detail : ''}</span></div>
                </div>
            )
        }
        return (
            <div key={s.id} className={'fo-stop' + (isNext ? ' is-next' : '') + (s.state === 'current' ? ' is-current' : '') + (selected === s.id ? ' is-sel' : '')}>
                <div className="fo-stop__head" onClick={() => { pick(s.id); open(s) }}>
                    <span className={'fo-num fo-num--' + s.tone}>{s.num}</span>
                    <div className="fo-stop__main">
                        <strong>{s.name}</strong>
                        <span><FaMapMarkerAlt /> {s.address}, {s.city}</span>
                        {s.eta ? <span className="fo-stop__eta"><FaLocationArrow /> {clockLabel(s.eta.arrive)} · {fmtMiles(s.eta.legMi)} · {fmtMinutes(s.eta.legMin)} from {s === pendingStops[0] ? 'here' : 'previous'}</span> : null}
                        {s.state === 'current' && s.ref.checkInAt ? <span className="fo-stop__eta">Checked in {clockOfStamp(s.ref.checkInAt)}</span> : null}
                    </div>
                    {isNext ? <Pill tone={s.state === 'current' ? 'cyan' : 'blue'}>{s.state === 'current' ? 'Here' : 'Next'}</Pill> : null}
                    {s.pending ? <Pill tone="amber">To send</Pill> : null}
                </div>
                {s.chips.length ? <div className="fo-stop__chips">{s.chips.slice(0, 4).map((c, i) => <Pill key={i} tone={c.tone}>{c.text}</Pill>)}</div> : null}
                <div className="fo-stop__actions">
                    <FoBtn kind={isNext ? 'primary' : 'soft'} icon={s.state === 'current' ? <FaStore /> : <FaStore />} onClick={() => open(s, s.state !== 'current')}>{s.state === 'current' ? 'Continue' : kind === 'rep' ? 'Check in' : 'Arrived'}</FoBtn>
                    <FoBtn kind="ghost" icon={<FaDirections />} href={mapsNavigateUrl(s.lat, s.lng)} newTab title="Open in Maps">Navigate</FoBtn>
                    <FoBtn kind="ghost" icon={<FaPhoneAlt />} href={telUrl(s.phone)} title="Call">Call</FoBtn>
                    <button type="button" className="fo-iconbtn" aria-label="More actions" onClick={() => setMenu(s)}><FaEllipsisH /></button>
                </div>
            </div>
        )
    }

    const listPane = (
        <div className="fo-route__list">
            {hero}
            {isToday && started && !submitted && new Date().getHours() >= 18 ? <Banner tone="warn" title="Do not forget to close your day.">It is past 6 PM and the day report has not been sent.</Banner> : null}
            {weekendNote ? <Banner tone="info">Today is a weekend. Showing the route of the next working day.</Banner> : null}
            {stops.length === 0
                ? <Empty title={kind === 'rep' ? 'No visits planned for this day' : 'No deliveries assigned for this day'} text={kind === 'rep' ? 'You can add a store yourself or ask the office to plan your week.' : 'The dispatcher has not assigned deliveries yet. Check the chat if you expected some.'} action={kind === 'rep' ? <FoBtn kind="primary" icon={<FaPlus />} onClick={() => setAddOpen(true)}>Add a stop</FoBtn> : <FoBtn kind="primary" onClick={() => nav.go('chat')}>Message the office</FoBtn>} />
                : (
                    <React.Fragment>
                        <div className="fo-listhead">
                            <h3>{kind === 'rep' ? 'Your route' : 'Your deliveries'}</h3>
                            <div className="fo-listhead__tools">
                                {kind === 'rep' ? <FoBtn kind="soft" small icon={<FaMagic />} onClick={optimize} disabled={!isToday || pendingStops.length < 3}>Optimize</FoBtn> : null}
                                {kind === 'rep' ? <FoBtn kind="soft" small icon={<FaPlus />} onClick={() => setAddOpen(true)}>Add stop</FoBtn> : null}
                                <FoBtn kind="soft" small icon={<FaDirections />} href={mapsRouteUrl(pendingStops.map((s) => ({ lat: s.lat, lng: s.lng })), anchor ? { lat: anchor.lat, lng: anchor.lng } : null)} newTab disabled={pendingStops.length === 0}>Route in Maps</FoBtn>
                            </div>
                        </div>
                        {stops.map(card)}
                    </React.Fragment>
                )}
        </div>
    )

    const mapCard = selectedStop ? (
        <div className="fo-mapcard">
            <div className="fo-mapcard__top"><span className={'fo-num fo-num--' + selectedStop.tone}>{selectedStop.state === 'done' ? '✓' : selectedStop.num}</span><div><strong>{selectedStop.name}</strong><span>{selectedStop.address}, {selectedStop.city}</span></div><Pill tone={selectedStop.tone}>{selectedStop.label}</Pill></div>
            {selectedStop.eta ? <div className="fo-mapcard__eta">ETA {clockLabel(selectedStop.eta.arrive)} · {fmtMiles(selectedStop.eta.legMi)} · {fmtMinutes(selectedStop.eta.legMin)}</div> : null}
            <div className="fo-mapcard__act">
                <FoBtn kind="primary" onClick={() => open(selectedStop, selectedStop.state === 'pending')}>{selectedStop.state === 'done' ? 'View visit' : selectedStop.state === 'current' ? 'Continue' : kind === 'rep' ? 'Check in' : 'Arrived'}</FoBtn>
                <FoBtn kind="ghost" icon={<FaDirections />} href={mapsNavigateUrl(selectedStop.lat, selectedStop.lng)} newTab>Navigate</FoBtn>
                <FoBtn kind="ghost" icon={<FaPhoneAlt />} href={telUrl(selectedStop.phone)}>Call</FoBtn>
            </div>
        </div>
    ) : selectedNearby ? (
        <div className="fo-mapcard">
            <div className="fo-mapcard__top"><span className="fo-num fo-num--purple">+</span><div><strong>{selectedNearby.store.name}</strong><span>{fmtMiles(selectedNearby.dist)} away · {selectedNearby.store.city}</span></div></div>
            <div className="fo-stop__chips">{selectedNearby.reasons.slice(0, 3).map((x, i) => <Pill key={i} tone={x.tone}>{x.text}</Pill>)}</div>
            <div className="fo-mapcard__act">
                {selectedNearby.unrouted ? <FoBtn kind="soft" onClick={() => f.send({ type: 'CHAT_SEND', actorKey: f.actor.key, from: 'worker', by: f.actor.name, text: 'Can you add “' + selectedNearby.store.name + '” (' + selectedNearby.store.city + ') to my route?', topic: 'schedule', ref: { type: 'store', id: selectedNearby.store.id, label: selectedNearby.store.name } }) || f.toast('Message sent to the office.')}>Ask the office</FoBtn> : (
                    <React.Fragment>
                        <FoBtn kind="primary" icon={<FaPlus />} onClick={() => { f.dispatch({ type: 'VISIT_EXTRA', storeId: selectedNearby.store.id, rep: f.actor.rep, date: date, source: 'nearby', nextUp: true }); f.toast(selectedNearby.store.name + ' added as your next stop.'); setSelected(null) }}>Add as next stop</FoBtn>
                        <FoBtn kind="soft" onClick={() => { f.dispatch({ type: 'VISIT_EXTRA', storeId: selectedNearby.store.id, rep: f.actor.rep, date: date, source: 'nearby' }); f.toast(selectedNearby.store.name + ' added at the end.'); setSelected(null) }}>At the end</FoBtn>
                    </React.Fragment>
                )}
                <FoBtn kind="ghost" icon={<FaDirections />} href={mapsNavigateUrl(selectedNearby.store.lat, selectedNearby.store.lng)} newTab>Navigate</FoBtn>
            </div>
        </div>
    ) : null

    const mapPane = (
        <div className="fo-route__map">
            <FieldMap pins={pins} dots={dots} lines={lines} me={gps.pos} depot={kind === 'driver' ? DEPOT : null} fitKey={fitKey + '-' + (narrow ? view : 'w') + (showNearby ? 'n' + dots.length : '')} focus={focus} legend
                onPick={(id) => { setSelected(id) }} paddingBottom={selectedStop || selectedNearby ? 150 : 0} emptyHint="No stops to show on the map.">
                <div className="fo-map__chips">
                    {kind === 'rep' ? <button type="button" className={'fo-maptoggle' + (showNearby ? ' is-on' : '')} onClick={() => { setShowNearby(!showNearby); setSelected(null) }}><FaPlus /> Nearby stores{showNearby && dotItems.length ? ' (' + dotItems.length + ')' : ''}</button> : null}
                    {road ? <span className="fo-maptoggle fo-maptoggle--info">{Math.round(road.miles)} mi · {fmtMinutes(road.minutes)} still to drive</span> : null}
                </div>
                {mapCard}
            </FieldMap>
        </div>
    )

    const segView = <Seg full value={view} onChange={setView} items={[{ key: 'list', label: 'List', icon: <FaList /> }, { key: 'map', label: 'Map', icon: <FaMap /> }]} />
    const mapFull = narrow && view === 'map'
    // the map takes the whole screen on phones / portrait tablets: no page scroll behind it
    useEffect(() => {
        if (!mapFull) return undefined
        const prev = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => { document.body.style.overflow = prev }
    }, [mapFull])

    return (
        <div className={'fo-route' + (narrow ? ' fo-route--narrow' : ' fo-route--wide')}>
            {narrow ? (
                view === 'list' ? (
                    <React.Fragment>
                        <div className="fo-route__seg">{segView}</div>
                        {listPane}
                    </React.Fragment>
                ) : (
                    <div className="fo-mapfull">
                        <div className="fo-mapfull__bar">
                            {segView}
                            <div className="fo-route__mapmini"><strong>{summary.done}/{Math.max(0, summary.total - summary.missed)} {kind === 'rep' ? 'visits' : 'delivered'}</strong>{nextStop ? <span>Next: #{nextNum} {nextStop.name}{nextStop.eta ? ' · ' + clockLabel(nextStop.eta.arrive) : ''}</span> : <span>{stops.length ? 'All done' : 'No stops'}</span>}</div>
                        </div>
                        <div className="fo-mapfull__map">{mapPane}</div>
                    </div>
                )
            ) : (<div className="fo-route__cols">{listPane}{mapPane}</div>)}

            {/* ---- stop menu ---- */}
            <Sheet show={!!menu} onClose={() => setMenu(null)} title={menu ? menu.name : ''} subtitle={menu ? (menu.state === 'pending' ? 'Stop #' + menu.num : menu.label) : ''}>
                {menu ? (
                    <div className="fo-menu">
                        <button type="button" disabled={!canMove(menu, -1)} onClick={() => { move(menu, -1); setMenu(null) }}>Move up one stop</button>
                        <button type="button" disabled={!canMove(menu, 1)} onClick={() => { move(menu, 1); setMenu(null) }}>Move down one stop</button>
                        <button type="button" onClick={() => { setResched(menu); setMenu(null) }}>{kind === 'rep' ? 'Reschedule this visit' : 'Postpone this delivery'}</button>
                        {kind === 'rep' ? <button type="button" onClick={() => { setSkip(menu); setMenu(null) }}>Skip it today</button> : null}
                        {kind === 'rep' && menu.extra ? <button type="button" className="is-danger" onClick={() => { f.dispatch({ type: 'VISIT_REMOVE', visitId: menu.id }); f.toast('Removed from today’s route.'); setMenu(null) }}>Remove (added by me)</button> : null}
                        <button type="button" onClick={() => { nav.push({ type: 'stop', kind: menu.kind, id: menu.id }); setMenu(null) }}>Open details</button>
                    </div>
                ) : null}
            </Sheet>

            <ConfirmSheet show={!!skip} title={skip ? 'Skip ' + skip.name + '?' : ''} subtitle="It counts as a missed visit and the office sees the reason." reasons={MISS_REASONS} confirmLabel="Skip this visit" tone="danger" onClose={() => setSkip(null)} onConfirm={doSkip} />
            {resched ? <RescheduleSheet stop={resched} kind={kind} onClose={() => setResched(null)} /> : null}

            <Sheet show={addOpen} onClose={() => setAddOpen(false)} title="Add a stop" subtitle="Stores near you that are not in today’s route" tall>
                <NearbyPanel anchor={nearbyAnchor} limit={10} onAdded={() => setAddOpen(false)} />
                <div className="fo-sheet__sep">Not nearby? Open the <strong>Stores</strong> tab to pick any of your stores.</div>
                <FoBtn kind="soft" block onClick={() => { setAddOpen(false); nav.go('stores') }}>Go to my stores</FoBtn>
            </Sheet>

            <Sheet show={!!optOpen} onClose={() => setOptOpen(false)} title="Optimize the order of the stops left" subtitle="Shortest driving path from where you are (it does not change finished stops)"
                footer={optOpen ? <React.Fragment><FoBtn kind="ghost" onClick={() => setOptOpen(false)}>Keep my order</FoBtn><FoBtn kind="primary" disabled={optOpen.before - optOpen.after < 0.5} onClick={() => { f.dispatch({ type: 'VISIT_REORDER', ids: optOpen.order.map((o) => o.id) }); f.toast('Route re-ordered. You save about ' + Math.round((optOpen.before - optOpen.after) * 1.28) + ' driving miles.'); setOptOpen(false) }}>Apply new order</FoBtn></React.Fragment> : null}>
                {optOpen ? (
                    <React.Fragment>
                        {optOpen.before - optOpen.after < 0.5
                            ? <Banner tone="ok">Your order is already about as short as it gets.</Banner>
                            : <Banner tone="ok" title={'Saves about ' + Math.round((optOpen.before - optOpen.after) * 1.28) + ' driving miles'}>{fmtMiles(optOpen.before * 1.28)} now → {fmtMiles(optOpen.after * 1.28)} with the new order.</Banner>}
                        <ol className="fo-optlist">{optOpen.order.map((o) => <li key={o.id}>{o.name}</li>)}</ol>
                    </React.Fragment>
                ) : null}
            </Sheet>
        </div>
    )
}

/** Choose a new day (and why) for a visit or a delivery. */
const RescheduleSheet = ({ stop, kind, onClose }) => {
    const f = useField()
    const options = []
    let d = f.date
    for (let i = 0; i < 6; i++) { d = addDays(d, 1); if (weekday(d) !== 0 && weekday(d) !== 6) options.push(d) }
    const [date, setDate] = useState(options[0])
    const [reason, setReason] = useState('')
    const [tried, setTried] = useState(false)
    const reasons = kind === 'rep' ? VISIT_RESCHED : RESCHEDULE_REASONS
    const save = () => {
        setTried(true)
        if (!reason) return
        if (kind === 'rep') f.dispatch({ type: 'VISIT_RESCHEDULE', visitId: stop.id, date: date, reason: reason })
        else f.dispatch({ type: 'DELIVERY_RESCHEDULE', id: stop.id, date: date, reason: reason, by: f.actor.name })
        f.toast(stop.name + ' moved to ' + fmtDate(date) + '.')
        onClose()
    }
    return (
        <Sheet show title={(kind === 'rep' ? 'Reschedule ' : 'Postpone ') + stop.name} subtitle="The office sees the new date and the reason" onClose={onClose}
            footer={<React.Fragment><FoBtn kind="ghost" onClick={onClose}>Cancel</FoBtn><FoBtn kind="primary" onClick={save}>Move it</FoBtn></React.Fragment>}>
            <Field label="New day">
                <div className="fo-chips">{options.map((o) => <button key={o} type="button" className={'fo-chip' + (date === o ? ' is-on' : '')} onClick={() => setDate(o)}>{fmtDate(o)}</button>)}</div>
            </Field>
            <Field label="Reason" error={tried && !reason ? 'Pick a reason' : ''}><ChipSelect options={reasons} value={reason} onChange={setReason} /></Field>
        </Sheet>
    )
}
